import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail, LOGO_URL } from "@/lib/api";
import { Truck, Loader2, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";

const AUTH_BG = "https://images.pexels.com/photos/5410923/pexels-photo-5410923.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client" });
  const [track, setTrack] = useState("");
  const [busy, setBusy] = useState(false);
  const [notRobot, setNotRobot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !notRobot) {
      toast.error('Confirma que "No soy un robot" para continuar');
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ ...form, role: "client" });
      toast.success("Bienvenido a TRIPLE M LOGISTICS");
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const goTrack = (e) => {
    e.preventDefault();
    if (track.trim()) nav(`/track/${track.trim().toUpperCase()}`);
  };

  const inputCls = "w-full bg-neutral-50 border border-neutral-300 rounded-none px-4 py-3 text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-corp focus:border-corp text-sm";

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-neutral-200 px-4 md:px-8 py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-6">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Triple M" className="w-12 h-12 border border-neutral-200 p-1 object-contain" />
          <div className="font-display font-black text-base md:text-lg tracking-tighter leading-none"><span className="text-corp">TRIPLE</span> <span className="text-freight">M</span> <span className="text-black">SERVICIOS LOGÍSTICOS</span></div>
        </div>
        <div className="text-xs md:text-sm text-neutral-600 md:ml-auto md:text-right leading-snug space-y-0.5">
          <div className="flex items-start gap-1.5 md:justify-end"><MapPin className="w-3.5 h-3.5 text-freight mt-0.5 shrink-0" /> Avda de las Américas 4, nave B1, 28823 Coslada · Madrid · España</div>
          <div className="flex items-center gap-1.5 md:justify-end"><Phone className="w-3.5 h-3.5 text-freight shrink-0" /> 91 441 24 24 · 671 34 23 82</div>
        </div>
      </header>
      <div className="flex-1 flex">
      {/* Left visual */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={AUTH_BG} alt="Camión de carga" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="Triple M" className="w-14 h-14 bg-white p-1 object-contain" />
            <div>
              <div className="font-display font-black text-white text-2xl tracking-tighter leading-none">TRIPLE M</div>
              <div className="font-mono text-[11px] tracking-[0.25em] text-freight mt-0.5">SERVICIOS LOGÍSTICOS</div>
            </div>
          </div>
          <h2 className="font-display text-4xl font-black text-white uppercase tracking-tighter leading-none max-w-md">
            Tu carga, entregada a tiempo.
          </h2>
          <p className="text-neutral-400 mt-4 max-w-sm text-sm">
            Gestión de envíos y seguimiento en tiempo real en una sola plataforma.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-slide-up">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src={LOGO_URL} alt="Triple M" className="w-12 h-12 border border-neutral-200 p-1 object-contain" />
            <div>
              <div className="font-display font-black text-xl tracking-tighter leading-none"><span className="text-corp">TRIPLE</span> <span className="text-freight">M</span></div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-black mt-0.5">SERVICIOS LOGÍSTICOS</div>
            </div>
          </div>

          <div className="flex mb-8 border border-neutral-300">
            <button data-testid="tab-login" onClick={() => setMode("login")}
              className={`flex-1 py-3 text-sm font-bold uppercase tracking-wide transition-colors ${mode === "login" ? "bg-corp text-white" : "text-neutral-500 hover:text-corp"}`}>
              Iniciar Sesión
            </button>
            <button data-testid="tab-register" onClick={() => setMode("register")}
              className={`flex-1 py-3 text-sm font-bold uppercase tracking-wide transition-colors ${mode === "register" ? "bg-corp text-white" : "text-neutral-500 hover:text-corp"}`}>
              Registrarse
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input data-testid="name-input" className={inputCls} placeholder="Nombre completo"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            )}
            <input data-testid="email-input" type="email" className={inputCls} placeholder="Correo electrónico"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <input data-testid="password-input" type="password" className={inputCls} placeholder="Contraseña"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            {mode === "register" && (
              <label data-testid="not-robot-check" className="flex items-center gap-3 border border-neutral-300 bg-neutral-50 px-4 py-3 cursor-pointer select-none">
                <input type="checkbox" checked={notRobot} onChange={(e) => setNotRobot(e.target.checked)}
                  className="w-5 h-5 accent-corp cursor-pointer" />
                <span className="text-sm font-medium text-neutral-700">No soy un robot</span>
              </label>
            )}
            <button data-testid="submit-auth-btn" type="submit" disabled={busy || (mode === "register" && !notRobot)}
              className="w-full bg-corp hover:bg-corp-hover text-white font-bold uppercase tracking-wide py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Entrar" : "Crear cuenta"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-neutral-200">
            <p className="text-neutral-500 text-xs font-bold uppercase tracking-wider mb-2">Rastrear un envío</p>
            <form onSubmit={goTrack} className="flex gap-2">
              <input data-testid="track-input" className={inputCls + " flex-1"} placeholder="TMXXX"
                value={track} onChange={(e) => setTrack(e.target.value)} />
              <button data-testid="track-btn" type="submit"
                className="border border-freight text-freight hover:bg-freight hover:text-black px-4 font-bold uppercase text-sm transition-colors">
                Buscar
              </button>
            </form>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
